import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, Download, Mail, Phone, User as UserIcon, ArrowUp, ArrowDown, Stethoscope } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNotification } from '../../contexts/NotificationContext';
import { AuthContext } from '../../contexts/AuthContext';
import { usePatientList } from './shared/services/usePatientService';

const ROWS_PER_PAGE = 5;

const PatientListPage = () => {
  const { userRole, permissions } = useContext(AuthContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const { patients, loading, deletePatient } = usePatientList();
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filterGender, setFilterGender] = useState('All');
  const [filterInsurance, setFilterInsurance] = useState('All');

  const hasPermission = (perm) => (userRole || '').toLowerCase() === 'admin' || (permissions || []).includes(perm);
  const showActionsColumn = hasPermission('Patients Surgery View') || hasPermission('Patients Edit') || hasPermission('Patients Delete');

  const filtered = patients.filter(p => {
    const matchesSearch = (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.mobile || '').toString().includes(searchTerm) ||
      (p.uhid_no || '').toString().includes(searchTerm) ||
      (p.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGender = filterGender === 'All' || p.sex === filterGender;
    const matchesInsurance = filterInsurance === 'All' || 
      (filterInsurance === 'Insurance' && (p.insurance_available === 1 || p.insurance_available === true || String(p.insurance_available).toLowerCase() === 'true')) ||
      (filterInsurance === 'Cash' && !(p.insurance_available === 1 || p.insurance_available === true || String(p.insurance_available).toLowerCase() === 'true'));

    return matchesSearch && matchesGender && matchesInsurance;
  });

  // Apply Sorting
  filtered.sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = (a[sortConfig.key] || '').toString().toLowerCase();
    const bVal = (b[sortConfig.key] || '').toString().toLowerCase();
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleExportPDF = async () => {
    const doc = new jsPDF();
    const logoUrl = '/logo.png';

    const toDataURL = (url) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });

    try {
      const logoData = await toDataURL(logoUrl);
      doc.addImage(logoData, 'PNG', 14, 10, 30, 30);
    } catch (error) {
      console.error('Failed to load logo image', error);
    }

    doc.setFontSize(18);
    doc.text('Patient List', 50, 25);

    const tableColumn = ["Patient Name", "Mobile No", "UHID No", "Email"];
    const tableRows = filtered.map(p => [p.name, p.mobile, p.uhid_no, p.email]);

    autoTable(doc, {
      startY: 45,
      head: [tableColumn],
      body: tableRows,
      headStyles: { fillColor: [0, 128, 128], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
    });

    doc.save('patient_list.pdf');
  };

  const handleDelete = (patient) => {
    showNotification('confirm', `Are you sure you want to delete patient ${patient.name}?`, async () => {
      try {
        await deletePatient(patient.uhid_no);
        showNotification('success', 'Patient deleted successfully');
      } catch (err) {
        showNotification('error', 'Failed to delete patient');
      }
    });
  };

  if (loading) return (
    <div className="w-full max-w-6xl mx-auto p-4 animate-pulse space-y-3">
      <div className="h-8 bg-gray-200 rounded mb-4"></div>
      {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-200 rounded"></div>)}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setSearchParams({ page: '0' }); }}
              className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all"
            />
          </div>
          <div className="flex flex-wrap sm:flex-nowrap gap-3">
            <select 
              value={filterGender} 
              onChange={(e) => { setFilterGender(e.target.value); setSearchParams({ page: '0' }); }}
              className="w-full sm:w-40 px-3 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none font-medium text-slate-700 cursor-pointer"
            >
              <option value="All">All Genders</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <select 
              value={filterInsurance} 
              onChange={(e) => { setFilterInsurance(e.target.value); setSearchParams({ page: '0' }); }}
              className="w-full sm:w-44 px-3 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none font-medium text-slate-700 cursor-pointer"
            >
              <option value="All">All Payments</option>
              <option value="Insurance">Insurance</option>
              <option value="Cash">Cash/Self</option>
            </select>
            {hasPermission('Patients Add') && (
              <button 
                onClick={() => navigate('/addPatient')} 
                className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-lg hover:bg-teal-700 font-bold transition-all shadow-sm active:scale-95 whitespace-nowrap"
              >
                <Plus className="h-4 w-4" /> Add Patient
              </button>
            )}
            <button 
              onClick={handleExportPDF} 
              className="flex items-center gap-2 bg-slate-700 text-white px-5 py-2.5 rounded-lg hover:bg-slate-800 font-bold transition-all shadow-sm active:scale-95 whitespace-nowrap"
            >
              <Download className="h-4 w-4" /> Export PDF
            </button>
          </div>
        </div>
      </div>

      <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
              {[
                { label: 'S/No', key: null },
                { label: 'Patient Name', key: 'name' },
                { label: 'Mobile', key: 'mobile' },
                { label: 'UHID No', key: 'uhid_no' },
                { label: 'Email', key: 'email' },
                showActionsColumn && { label: 'Actions', key: null }
              ].filter(Boolean).map(h => (
                <th 
                  key={h.label} 
                  onClick={() => h.key && requestSort(h.key)}
                  className={`px-4 py-4 font-bold text-slate-700 text-sm tracking-tight ${h.key ? 'cursor-pointer select-none hover:bg-slate-100/50' : ''} transition-colors`}
                >
                  <div className="flex items-center gap-1.5">
                    {h.label}
                    {sortConfig.key === h.key && (
                      <div className="text-teal-600">
                        {sortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={showActionsColumn ? 6 : 5} className="p-12 text-center text-gray-500">No patients found</td></tr>
            ) : paginated.map((p, i) => (
              <tr key={p.uhid_no} className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                <td className="p-4">{currentPage * ROWS_PER_PAGE + i + 1}</td>
                <td className="p-4 font-medium">{p.name}</td>
                <td className="p-4"><div className="flex items-center gap-2 text-gray-600"><Phone className="h-4 w-4" />{p.mobile}</div></td>
                <td className="px-4 py-4 text-slate-500 font-mono text-sm">{p.uhid_no}</td>
                <td className="px-4 py-4 text-slate-600"><div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" />{p.email}</div></td>
                {showActionsColumn && (
                  <td className="px-4 py-4">
                    <div className="flex gap-2 items-center">
                      {hasPermission('Patients Surgery View') && (
                        <button 
                          onClick={() => navigate(`/mainPage/${p.uhid_no}`, {
                            state: { activeMenu: 'Patients', page: currentPage }
                          })} 
                          className="flex items-center gap-2 px-3 py-2 text-teal-600 hover:bg-teal-50 border border-teal-200 rounded-lg transition-colors font-medium" 
                          title="Surgery Test"
                        >
                          <Stethoscope className="h-4 w-4" />
                          Surgery
                        </button>
                      )}
                      {hasPermission('Patients Edit') && (
                        <button 
                          onClick={() => navigate(`/editPatient/${p.uhid_no}`, { 
                            state: { activeMenu: 'Patients', page: currentPage } 
                          })} 
                          className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors font-medium" 
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                      {hasPermission('Patients Delete') && (
                        <button 
                          onClick={() => handleDelete(p)} 
                          className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium" 
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginated.map((p, i) => (
          <div key={p.uhid_no} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 text-lg mb-2">{p.name}</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2"><Phone className="h-4 w-4" />{p.mobile}</div>
              <div className="flex items-center gap-2"><UserIcon className="h-4 w-4" />UHID: {p.uhid_no}</div>
              <div className="flex items-center gap-2"><Mail className="h-4 w-4" />{p.email}</div>
            </div>
            <div className="flex gap-2 pt-3 mt-4 border-t border-gray-100 flex-wrap">
              {hasPermission('Patients Surgery View') && (
                <button 
                  onClick={() => navigate(`/mainPage/${p.uhid_no}`, {
                    state: { activeMenu: 'Patients', page: currentPage }
                  })} 
                  className="flex items-center justify-center gap-2 px-3 py-2 text-teal-600 border border-teal-200 rounded-lg text-sm font-medium hover:bg-teal-50 transition-colors"
                >
                  <Stethoscope className="h-4 w-4" />
                  Surgery
                </button>
              )}
              {hasPermission('Patients Edit') && (
                <button 
                  onClick={() => navigate(`/editPatient/${p.uhid_no}`, { 
                    state: { activeMenu: 'Patients', page: currentPage } 
                  })} 
                  className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
              {hasPermission('Patients Delete') && (
                <button 
                  onClick={() => handleDelete(p)} 
                  className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filtered.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">Showing {currentPage * ROWS_PER_PAGE + 1}–{Math.min((currentPage + 1) * ROWS_PER_PAGE, filtered.length)} of {filtered.length}</div>
          <div className="flex gap-2">
            <button onClick={() => setSearchParams({ page: '0' })} disabled={currentPage === 0} className="p-2 border rounded-lg disabled:opacity-50 text-xs font-bold uppercase tracking-widest">First</button>
            <button onClick={() => setSearchParams({ page: Math.max(0, currentPage - 1).toString() })} disabled={currentPage === 0} className="p-2 border rounded-lg disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
            <span className="px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium">{currentPage + 1} of {totalPages}</span>
            <button onClick={() => setSearchParams({ page: Math.min(totalPages - 1, currentPage + 1).toString() })} disabled={currentPage >= totalPages - 1} className="p-2 border rounded-lg disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
            <button onClick={() => setSearchParams({ page: (totalPages - 1).toString() })} disabled={currentPage >= totalPages - 1} className="p-2 border rounded-lg disabled:opacity-50 text-xs font-bold uppercase tracking-widest">Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientListPage;
